// src/client/plugins/retry.ts
import { ClientPlugin, RequestConfig, RetryPluginConfig } from "../types";

const DEFAULT_RETRY_CONFIG: Required<RetryPluginConfig> = {
  enabled: true,
  maxRetries: 3,
  retryDelay: 1000,
  retryOnNetworkError: true,
  retryOnTimeout: true,
  retryableStatusCodes: [408, 429, 502, 503, 504],
};

export class RetryPlugin implements ClientPlugin {
  name = "retry";
  private config: Required<RetryPluginConfig>;

  constructor(config: RetryPluginConfig = {}) {
    this.config = { ...DEFAULT_RETRY_CONFIG, ...config };
  }

  async onError(error: Error, config: RequestConfig) {
    if (!this.config.enabled) {
      throw error;
    }

    let retries = 0;
    const shouldRetry = this.shouldRetryError(error);

    while (shouldRetry && retries < this.config.maxRetries) {
      try {
        await this.delay(this.getRetryDelay(retries));
        return await config.client.query(config.operation.key, config.variables);
      } catch (retryError) {
        retries++;
        if (retries === this.config.maxRetries) {
          throw retryError;
        }
      }
    }

    throw error;
  }

  private shouldRetryError(error: any): boolean {
    if (!this.config.enabled) return false;

    // Network errors
    if (
      this.config.retryOnNetworkError &&
      error.message.includes("Network Error")
    ) {
      return true;
    }

    // Timeout errors
    if (this.config.retryOnTimeout && error.message.includes("Timeout")) {
      return true;
    }

    // Status code based retry
    if (
      error.response?.status &&
      this.config.retryableStatusCodes.includes(error.response.status)
    ) {
      return true;
    }

    return false;
  }

  private getRetryDelay(attempt: number): number {
    return this.config.retryDelay * Math.pow(2, attempt);
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}
